import {
  ArtistAboutStatus,
  ArtistSubmitParams,
  getArtistAboutDetail,
  publishArtistAbout,
  rejectArtistAbout,
} from '@/api/artistAbout';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { useQuery } from '@tanstack/react-query';
import { Button, Flex, Input, message, Space } from 'antd';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RejectDialog from './rejectDialog';
import { EditOutlined } from '@ant-design/icons';
import { ensureHttpPrefix } from '@/utils/http';

const ArtistAboutEditor = () => {
  const isMobile = useAtomValue(isMobileAtom);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [currentId, setCurrentId] = useState('');
  const [introduction, setIntroduction] = useState('');
  const [introductionEdit, setIntroductionEdit] = useState(false);
  const [resume, setResume] = useState('');
  const [resumeEdit, setResumeEdit] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const labelStyle: React.CSSProperties = {
    fontWeight: 'bold',
    padding: isMobile ? 0 : '0 10px',
    flexShrink: 0,
  };

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      setCurrentId(id);
    }
  }, [searchParams]);

  const { data: artistAbout, refetch: refetchArtistAbout } = useQuery({
    queryKey: ['artistAbout'],
    queryFn: async () => {
      const res = await getArtistAboutDetail(currentId);
      if (res.status !== 200) {
        messageApi.error(res.message);
      }
      setIntroduction(res.data.introduction);
      setResume(res.data.resume);
      return res.data;
    },
    enabled: !!currentId,
    refetchOnWindowFocus: false,
  });

  const handleApprove = async () => {
    const params: ArtistSubmitParams = {
      id: currentId,
      introduction,
      resume,
    };
    const res = await publishArtistAbout(params);
    if (res.status !== 200) {
      messageApi.error(res.message);
    } else {
      messageApi.success('发布成功');
      void refetchArtistAbout();
    }
  };

  const handleReject = () => {
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async (reason: string[], remark: string) => {
    const params: ArtistSubmitParams = {
      id: currentId,
      introduction,
      resume,
      rejectReason: reason,
      rejectReasonText: remark,
    };
    const resp = await rejectArtistAbout(params);
    if (resp.status !== 200) {
      messageApi.error(resp.message);
    } else {
      messageApi.success('驳回成功');
      setRejectDialogOpen(false);
      void refetchArtistAbout();
    }
  };

  return (
    <div>
      {contextHolder}
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>信息 ID</div>
        <div>{currentId}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>SUPP ID</div>
        <div>{artistAbout?.userNumber}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>用户名</div>
        <div>{artistAbout?.nickName}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>真实姓名</div>
        <div>{artistAbout?.name}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: 'column',
        }}
        gap={10}
      >
        <Flex align="center">
          <div style={labelStyle}>简介</div>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setIntroductionEdit(true);
            }}
          />
        </Flex>
        <Input.TextArea
          style={{ margin: '0 10px' }}
          value={introduction}
          disabled={!introductionEdit}
          onChange={(e) => {
            setIntroduction(e.target.value);
          }}
          rows={4}
        />
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: 'column',
        }}
        gap={10}
      >
        <Flex align="center">
          <div style={labelStyle}>履历</div>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => setResumeEdit(true)}
          />
        </Flex>
        <Input.TextArea
          style={{ margin: '0 10px' }}
          value={resume}
          disabled={!resumeEdit}
          onChange={(e) => {
            setResume(e.target.value);
          }}
          rows={6}
        />
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
        gap={10}
      >
        <div style={labelStyle}>个人主页</div>
        <Flex vertical gap={5} style={{ wordBreak: 'break-all' }}>
          {artistAbout?.facebookUrl && (
            <a
              href={ensureHttpPrefix(artistAbout?.facebookUrl)}
              target="_blank"
              rel="noreferrer"
            >
              {artistAbout?.facebookUrl}
            </a>
          )}
          {artistAbout?.instagramUrl && (
            <a
              href={ensureHttpPrefix(artistAbout?.instagramUrl)}
              target="_blank"
              rel="noreferrer"
            >
              {artistAbout?.instagramUrl}
            </a>
          )}
          {artistAbout?.twitterUrl && (
            <a
              href={ensureHttpPrefix(artistAbout?.twitterUrl)}
              target="_blank"
              rel="noreferrer"
            >
              {artistAbout?.twitterUrl}
            </a>
          )}
          {artistAbout?.websiteUrl && (
            <a
              href={ensureHttpPrefix(artistAbout?.websiteUrl)}
              target="_blank"
              rel="noreferrer"
            >
              {artistAbout?.websiteUrl}
            </a>
          )}
        </Flex>
      </Flex>
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Space size="small">
          <Button
            type="primary"
            onClick={handleApprove}
            disabled={artistAbout?.status !== ArtistAboutStatus.PENDING}
          >
            发布
          </Button>
          <Button
            type="primary"
            danger
            onClick={handleReject}
            disabled={artistAbout?.status !== ArtistAboutStatus.PENDING}
          >
            驳回
          </Button>
          <Button onClick={() => navigate('/artist-about')}>取消</Button>
        </Space>
      </div>
      <RejectDialog
        isOpen={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
      />
    </div>
  );
};

export default ArtistAboutEditor;
